import type { Disposable, IEnvironment } from 'relay-runtime';

import type { useAgentSessionsDataPaginationQuery$variables } from './__generated__/useAgentSessionsDataPaginationQuery.graphql';
import { refreshAgentSessions } from './refreshAgentSessions';

export type AgentSessionsLiveReader = {
	environment: IEnvironment;
	key: string;
	variables: useAgentSessionsDataPaginationQuery$variables;
	loadedExtent: number;
	operation: Disposable | null;
};

/** One event listener and background refresh for readers of the same Relay connection. */
const createGroup = ({ environment, variables }: AgentSessionsLiveReader) => {
	const readers = new Set<AgentSessionsLiveReader>();
	let observers = 0;
	let pending = false;
	let work: Disposable | undefined;
	let retained: Disposable | undefined;
	let subscription: Disposable | undefined;

	const cancel = () => {
		if (work != null) {
			const previous = work;
			work = undefined;
			pending = true;
			previous.dispose();
		}
	};
	const canRefresh = () =>
		observers > 0 &&
		readers.size > 0 &&
		pending &&
		!Array.from(readers).some((reader) => reader.operation != null) &&
		document.visibilityState === 'visible';

	const schedule = () => {
		if (work != null || !canRefresh()) return;
		const timer = setTimeout(() => {
			work = undefined;
			if (!canRefresh()) return;
			pending = false;
			const pageSize = Math.max(
				...Array.from(readers, (reader) =>
					Math.max(reader.variables.pageSize ?? 25, reader.loadedExtent),
				),
			);
			let request: Disposable | undefined;
			const job = { dispose: () => request?.dispose() };
			work = job;
			const finish = (error: Error | null) => {
				if (work !== job) return;
				work = undefined;
				if (error != null) {
					job.dispose();
				} else {
					retained?.dispose();
					retained = job;
				}
				// Only an event received during this request warrants another refresh.
				schedule();
			};
			try {
				request = refreshAgentSessions(
					environment,
					{ ...variables, pageSize, after: null },
					finish,
				);
				if (work !== job && retained !== job) request.dispose();
			} catch (error) {
				finish(error instanceof Error ? error : new Error('Session refresh failed'));
			}
		}, 1000);
		work = { dispose: () => clearTimeout(timer) };
	};
	const invalidate = () => {
		pending = true;
		schedule();
	};
	const resume = () => {
		if (document.visibilityState === 'visible') invalidate();
	};
	const stop = () => {
		subscription?.dispose();
		subscription = undefined;
		cancel();
		window.removeEventListener('online', resume);
		document.removeEventListener('visibilitychange', resume);
	};
	return {
		readers,
		cancel,
		schedule,
		observe: (subscribe: (onEvent: () => void) => Disposable) => {
			if (observers++ === 0) {
				subscription = subscribe(invalidate);
				window.addEventListener('online', resume);
				document.addEventListener('visibilitychange', resume);
			}
			schedule();
			return () => {
				if (--observers === 0) stop();
			};
		},
		dispose: () => {
			// Private and SSR readers never attach browser listeners.
			if (observers > 0) stop();
			cancel();
			retained?.dispose();
		},
	};
};

const environments = new WeakMap<IEnvironment, Map<string, ReturnType<typeof createGroup>>>();
const find = (reader: AgentSessionsLiveReader) =>
	environments.get(reader.environment)?.get(reader.key);

const register = (reader: AgentSessionsLiveReader): (() => void) => {
	let groups = environments.get(reader.environment);
	if (groups == null) {
		groups = new Map();
		environments.set(reader.environment, groups);
	}
	const group = groups.get(reader.key) ?? createGroup(reader);
	groups.set(reader.key, group);
	group.readers.add(reader);
	return () => {
		reader.operation?.dispose();
		group.readers.delete(reader);
		if (group.readers.size === 0) {
			group.dispose();
			if (groups.get(reader.key) === group) groups.delete(reader.key);
		}
	};
};

const observe = (
	reader: AgentSessionsLiveReader,
	subscribe: (onEvent: () => void) => Disposable,
): (() => void) => find(reader)?.observe(subscribe) ?? (() => {});

// User requests stay with Relay and start immediately; only background work is cancelled.
const pause = (reader: AgentSessionsLiveReader): void => find(reader)?.cancel();
const resume = (reader: AgentSessionsLiveReader): void => find(reader)?.schedule();

export const agentSessionsLiveCoordinator: Readonly<{
	register: typeof register;
	observe: typeof observe;
	pause: typeof pause;
	resume: typeof resume;
}> = { register, observe, pause, resume };
